import { useState, useEffect, useCallback } from "react";
import { ipGet, ipError } from "../api";
import { PlannerError } from "../components/PlannerState";
import { Card, PageHeader, KpiRow, Badge } from "../../components/dc";

const gbp = (n) => (typeof n === "number" ? `£${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—");

// Phase 4: a £ lens on the latest forecast, pulled read-only from PriceGuard. Each
// block degrades to "not available" if its reader is not wired (standalone).
export default function CostSupply() {
  const [summary, setSummary] = useState(null);
  const [recon, setRecon] = useState(null);
  const [caps, setCaps] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setError(""); setLoading(true);
    Promise.all([ipGet("/cost-supply/capabilities"), ipGet("/cost-supply/summary"), ipGet("/cost-supply/reconciliation")])
      .then(([cap, s, r]) => { setCaps(cap); setSummary(s?.ok ? s : null); setRecon(r?.ok ? r : null); })
      .catch(e => setError(ipError(e))).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const v = summary?.value;
  const capBadge = (ok) => <Badge tone={ok ? "green" : "muted"}>{ok ? "connected" : "not available"}</Badge>;

  return (
    <div data-testid="ip-cost-supply-page" style={{ minWidth: 0 }}>
      <PageHeader title="Cost & Supply" subtitle="Forecast priced up from PriceGuard, with supplier mix and a volume sanity check. Read-only links; nothing is written back." />
      <PlannerError message={error} testId="ip-cost-error" retry={load} />
      {loading && <p role="status" data-testid="ip-cost-loading">Loading cost and supply data…</p>}
      {summary?.price_basis && <p data-testid="ip-cost-price-basis">{summary.price_basis}</p>}

      {caps && (
        <Card style={{ marginBottom: 14 }}>
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap", fontSize: 13 }}>
            <span>Prices {capBadge(caps.unit_price)}</span>
            <span>Invoices {capBadge(caps.invoice_volumes)}</span>
            <span>BOM {capBadge(caps.bom)}</span>
            <span>Suppliers {capBadge(caps.suppliers)}</span>
          </div>
        </Card>
      )}

      {!v ? null : v.available === false ? (
        <Card><p style={{ color: "var(--color-text-3)", margin: 0 }}>Priced-up forecast is not available ({v.reason}). The forecast itself is unaffected.</p></Card>
      ) : (
        <>
          <div data-testid="ip-cost-kpis"><KpiRow items={[
            { label: "Forecast value", value: gbp(v.total_value) },
            { label: "Price coverage", value: typeof v.price_coverage_pct === "number" ? `${v.price_coverage_pct}%` : "—" },
            { label: "Priced parts", value: String(v.priced_parts) },
            { label: "Unpriced parts", value: String(v.unpriced_parts) },
          ]} cols={4} /></div>

          {summary.by_supplier?.length > 0 && (
            <Card style={{ marginTop: 14 }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Forecast value by supplier</div>
              <div style={{ overflowX: "auto" }}><table data-testid="ip-cost-suppliers" className="data-table" style={{ width: "100%", fontSize: 13 }}>
                <thead><tr><th>Supplier</th><th style={{ textAlign: "right" }}>Value</th></tr></thead>
                <tbody>{summary.by_supplier.map((s) => (
                  <tr key={s.supplier}><td>{s.supplier}</td><td style={{ textAlign: "right" }}>{gbp(s.value)}</td></tr>
                ))}</tbody>
              </table></div>
            </Card>
          )}
        </>
      )}

      <Card style={{ marginTop: 14 }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Volume reconciliation (forecast vs invoiced)</div>
        {recon?.since && <p data-testid="ip-reconciliation-period">Invoice period: {recon.since} to {recon.until} · All DCs</p>}
        {!recon ? (
          <p data-testid="ip-cost-empty" style={{ color: "var(--color-text-3)", margin: 0 }}>{loading ? "Loading…" : error ? "Reconciliation is unavailable." : "No run yet."}</p>
        ) : recon.available === false ? (
          <p style={{ color: "var(--color-text-3)", margin: 0 }}>Not available ({recon.reason}).</p>
        ) : (
          <div style={{ overflowX: "auto" }}><table data-testid="ip-cost-reconciliation" className="data-table" style={{ width: "100%", fontSize: 13 }}>
            <thead><tr><th>Part</th><th style={{ textAlign: "right" }}>Forecast</th><th style={{ textAlign: "right" }}>Invoiced</th><th style={{ textAlign: "right" }}>Variance</th></tr></thead>
            <tbody>{(recon.rows || []).slice(0, 40).map((r, i) => (
              <tr key={i}><td style={{ fontFamily: "monospace" }}>{r.part}</td>
                <td style={{ textAlign: "right" }}>{r.forecast}</td>
                <td style={{ textAlign: "right" }}>{r.invoiced}</td>
                <td style={{ textAlign: "right" }}>{r.variance > 0 ? "+" : ""}{r.variance}</td></tr>
            ))}</tbody>
          </table></div>
        )}
      </Card>
    </div>
  );
}
